// GA4 do painel — mede AUDIÊNCIA PÚBLICA (espectador de live/replay, visitante
// do buyflow de apoio), não produto. Analítica de produto do app segue no
// Firebase Analytics (`mobile/lib/services/metrics_service.dart`), em outra
// propriedade; erro/diagnóstico segue no Sentry (`lib/sentry.ts`).
//
// Quatro regras, que valem também nos outros dois artefatos que carregam a
// mesma tag — a landing (`landing/public/landing.js`) e o viewer vanilla de
// party (`infra/dashboard/live/index.html`):
//
//  1. **Um measurement ID pros três.** Sessão e usuário só são compartilhados
//     DENTRO de um stream: streams separados fariam a mesma pessoa virar dois
//     usuários ao pular da landing pro live. A separação por site sai de graça
//     no relatório, pelas dimensões hostname e page path.
//  2. **Só nos hosts de produção.** localhost, preview e servidor de dev ficam
//     fora — audiência suja não serve como número de mídia (patrocínio).
//  3. **Só nas rotas públicas.** O painel do organizador fica de fora de
//     propósito: audiência de operador único não diz nada, e `page_location` de
//     `/business/sessions/:id` levaria o id da sessão de um atleta pro Google.
//  4. **`page_view` não é enviado manualmente.** O Enhanced Measurement do
//     stream já dispara em history change (o que cobre a navegação da SPA);
//     mandar daqui dobraria a contagem.
//
// O measurement ID é público por natureza (vai no bundle e no HTML servido),
// então mora no código e não em `VITE_…`: variável esquecida no deploy vira
// métrica ausente em silêncio, que é justamente o que não se percebe.

const MEASUREMENT_ID = 'G-V0KKBLZZEM';

/// Hosts de produção do painel. Domínio novo (ou preview permanente) só passa a
/// medir quando entrar nesta lista — mesma disciplina da restrição de referrer
/// da chave do Maps. Lembrar da CSP em `infra/nginx/admin.conf`: sem
/// `googletagmanager.com` liberado, a tag é bloqueada sem erro visível.
const MEASURED_HOSTS = ['events.windmaps.app', 'events.faromaps.app'];

/// Rotas de público externo. Tudo que não casa aqui é painel interno.
///
/// `/privacy` saiu em 02/09/2026: a política virou HTML estático na landing e
/// esta rota é só um `location.replace` pra fora. Medir aqui subiria a tag e
/// dispararia um `page_view` numa página que descarrega no mesmo instante,
/// dobrando a contagem com o hit da própria landing (que tem GA4 dela).
const MEASURED_PATHS = ['/live', '/replay', '/apoio', '/terms'];

type GtagFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
  }
}

/// Puro de propósito (host + path entram como argumento): a decisão de medir é
/// a parte que precisa ficar óbvia na leitura e verificável sem browser.
export function isMeasuredLocation(hostname: string, pathname: string): boolean {
  if (!MEASURED_HOSTS.includes(hostname)) return false;
  return MEASURED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

let injected = false;

/// Injeta o gtag quando a página atual é pública e está num host de produção.
/// No-op silencioso caso contrário — e no-op é o caminho esperado em dev.
export function initAnalytics(loc: Pick<Location, 'hostname' | 'pathname'> = window.location) {
  if (injected) return;
  if (!isMeasuredLocation(loc.hostname, loc.pathname)) return;
  injected = true;

  const tag = document.createElement('script');
  tag.async = true;
  tag.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(tag);

  const layer: unknown[] = (window.dataLayer = window.dataLayer ?? []);
  window.gtag = function () {
    // O snippet oficial empurra o objeto `arguments`, e é por ele que o gtag.js
    // reconhece um comando; um array comum entra no dataLayer e é ignorado em
    // silêncio. Por isso o rest param não serve aqui.
    // eslint-disable-next-line prefer-rest-params
    layer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', MEASUREMENT_ID);
}

/// Evento personalizado do GA4.
///
/// **No-op quando não há `gtag`**, que é o caminho normal em dev e no painel
/// interno: o `initAnalytics` só injeta a tag em host de produção E rota
/// pública (regras 2 e 3 do cabeçalho), então a ausência do global é
/// exatamente o sinal de "aqui não se mede". Chamar isto de dentro de um
/// componente que roda nas duas superfícies é seguro por construção — não
/// precisa de guarda no call site.
///
/// Não substitui `page_view` (regra 4): serve pra evento que o Enhanced
/// Measurement não tem como saber, como a montagem do mapa (#966), cuja
/// contagem é o que prova que o custo de map load saiu do Google.
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  // `typeof window` e não só o optional chaining: sem `window` no escopo (a
  // Camada 3a roda em `node`), `window.gtag?.()` lança ReferenceError — o `?.`
  // protege da propriedade ausente, não do objeto ausente.
  if (typeof window === 'undefined') return;
  window.gtag?.('event', name, params);
}
